import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, MenuItem, Typography, Box, Card, CardMedia, Button, TextField } from "@mui/material";
import {
  FormContainer,
  HeaderTitle,
  StyledTextField,
  ActionButton,
} from "./CreateProductForm.styles";

import { 
  createProduct, 
  getCategories, 
  getManufacturerList,
  extractImagesByProductDetails,
  runAIInferenceByProductDetails,
  uploadCustomImage,
  getVendors,
  selectRegularProductImages
} from "../../api/api";
import {useToast} from '../../components/ToastProvider/ToastProvider';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const CONDITION_OPTIONS = [
  'New',
  'F/S',
  'NOB',
  'Refurbished',
  'Refurbished - Not Audited',
  'Factory Refurbished'
];

export default function CreateProductForm({ onCreated, onCancel }) {
  const { showToast } = useToast();
  const navigate = useNavigate(); 

  const [formData, setFormData] = useState({
    mfr: "",
    description: "",
    location: "",
    category: "",   // holds category_id
    condition: "",
    vendor: "",    // Vendor name
    qty: "",
    cost_price: "",  // Renamed from apid
    retail_price: "", // Renamed from apid_retail
    weight: "",
    dimensions: "",
    ai_low: "",
    ai_high: "",
    subpn: "",
    sub2pn: "",
    part: "",       // SKU
    concatenate_code: "", // Auto-generated
    specifications: "", // Long description
  });

  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingMfrs, setLoadingMfrs] = useState(true);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Image management state
  const [extractedImages, setExtractedImages] = useState([]);
  const [customImages, setCustomImages] = useState([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [confirmedImages, setConfirmedImages] = useState([]);
  const fileInputRef = useRef(null);

  // AI inference state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInferenceData, setAiInferenceData] = useState(null);

  // fetch categories once
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingCats(true);
        const list = await getCategories("skip=0&limit=200");
        if (!alive) return;
        setCategories(Array.isArray(list) ? list : []);
      } catch {
        if (alive) setCategories([]);
      } finally {
        if (alive) setLoadingCats(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // fetch manufacturers once
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingMfrs(true);
        const list = await getManufacturerList();
        if (!alive) return;
        setManufacturers(Array.isArray(list) ? list : []);
      } catch {
        if (alive) setManufacturers([]);
      } finally {
        if (alive) setLoadingMfrs(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // fetch vendors once
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingVendors(true);
        const list = await getVendors();
        if (!alive) return;
        setVendors(Array.isArray(list) ? list : []);
      } catch {
        if (alive) setVendors([]);
      } finally {
        if (alive) setLoadingVendors(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const catOptions = useMemo(() => {
    if (loadingCats) return [{ value: "", label: "Loading…" }];
    return [
      { value: "", label: "(none)" },
      ...categories.map((c) => ({ value: String(c.id), label: c.name })),
    ];
  }, [categories, loadingCats]);

  const mfrOptions = useMemo(() => {
    if (loadingMfrs) return [{ value: "", label: "Loading…" }];
    return [
      { value: "", label: "(none)" },
      ...manufacturers.map((m) => ({ value: String(m.id), label: m.name })),
    ];
  }, [manufacturers, loadingMfrs]);

  const vendorOptions = useMemo(() => {
    if (loadingVendors) return [{ value: "", label: "Loading…" }];
    return [
      { value: "", label: "(none)" },
      ...vendors.map((v) => ({ value: v, label: v })),
    ];
  }, [vendors, loadingVendors]);

  // Generate concatenate code based on vendor-specific logic
  const generateConcatenateCode = useMemo(() => {
    const vendor = formData.vendor?.trim() || "";
    const mfrId = formData.mfr?.trim() || "";
    const part = formData.part?.trim() || "";
    const condition = formData.condition?.trim() || "";

    // All required fields must be present
    if (!vendor || !mfrId || !part || !condition) {
      return "";
    }

    // Get vendor name for concatenate code
    let vendorName = "";
    const vendorLower = vendor.toLowerCase().trim();
    if (vendorLower === "mfp-technology" || vendorLower === "mfp technology") {
      vendorName = "MFP";
    } else if (vendorLower === "sourcing-it" || vendorLower === "sourcing it") {
      vendorName = "SourcingIT";
    } else if (vendorLower === "lenovo-top-choice" || vendorLower === "lenovo top choice" || vendorLower === "lenovo") {
      vendorName = "Lenovo";
    } else if (vendorLower === "lcr") {
      vendorName = "LCR";
    } else {
      // Default: capitalize first letter of each word, remove spaces/hyphens
      vendorName = vendor.trim().replace(/-/g, " ").replace(/_/g, " ").split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join("")
        .replace(/\s+/g, "");
    }

    // Get MFR/Brand name
    const selectedManufacturer = manufacturers.find(m => m.id === Number(mfrId));
    const mfrBrand = selectedManufacturer ? selectedManufacturer.name : "";

    if (!mfrBrand) {
      return "";
    }

    // Get part number (trim extra zeros for MFP if needed)
    let partNo = part.trim();
    if ((vendorLower === "mfp-technology" || vendorLower === "mfp technology") && partNo) {
      // Trim leading zeros for MFP
      partNo = partNo.replace(/^0+/, "") || partNo;
    }

    if (!partNo) {
      return "";
    }

    // Get condition (with special handling for Lenovo and LCR)
    let finalCondition = condition.trim();
    if (vendorLower === "lenovo-top-choice" || vendorLower === "lenovo top choice" || vendorLower === "lenovo") {
      finalCondition = "New";
    } else if (vendorLower === "lcr") {
      finalCondition = "Refurbished";
    }

    if (!finalCondition) {
      return "";
    }

    // Generate concatenate code: {vendor}-{mfr/brand}-{partnumber}-{condition}
    return `${vendorName}-${mfrBrand}-${partNo}-${finalCondition}`;
  }, [formData.vendor, formData.mfr, formData.part, formData.condition, manufacturers]);

  // Update concatenate_code when dependencies change
  useEffect(() => {
    setFormData(prev => {
      // Always update concatenate_code when generateConcatenateCode changes
      // This ensures it updates even when it becomes empty
      if (prev.concatenate_code !== generateConcatenateCode) {
        return { ...prev, concatenate_code: generateConcatenateCode };
      }
      return prev;
    });
  }, [generateConcatenateCode]);

  // Check if required fields are filled
  const isFormValid = useMemo(() => {
    return !!(
      formData.vendor?.trim() &&
      formData.mfr?.trim() &&
      formData.part?.trim() &&
      formData.condition?.trim()
    );
  }, [formData.vendor, formData.mfr, formData.part, formData.condition]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => {
      const updated = { ...s, [name]: value };
      
      // Auto-calculate retail_price when cost_price changes
      if (name === 'cost_price' && value) {
        const costPrice = parseFloat(value);
        if (!isNaN(costPrice) && costPrice > 0) {
          const calculatedRetail = costPrice / 0.85;
          updated.retail_price = calculatedRetail.toFixed(2);
        } else {
          // If cost_price is invalid or empty, clear retail_price
          updated.retail_price = '';
        }
      }
      
      return updated;
    });

    if (error) setError(null);
  };

  const toInt = (v, def = 0) => {
    if (v === "" || v === null || v === undefined) return def;
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };

  const toFloat = (v, def = null) => {
    if (v === "" || v === null || v === undefined) return def;
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };

  // Image handling functions
  const handleExtractImages = async () => {
    if (!formData.description.trim()) {
      showToast('Please enter product description first', 'warning');
      return;
    }

    setImageLoading(true);
    try {
      const selectedManufacturer = manufacturers.find(mfr => mfr.id === Number(formData.mfr));
      const selectedCategory = categories.find(cat => cat.id === Number(formData.category));
      
      const brand = selectedManufacturer ? selectedManufacturer.name : formData.mfr.trim();
      const category = selectedCategory ? selectedCategory.name : '';
      
      const productDetails = {
        brand: brand,
        description: formData.description.trim(),
        sku: formData.part?.trim() || '',
        category: category
      };
      
      const response = await extractImagesByProductDetails(productDetails);
      
      if (response.success && response.images && response.images.length > 0) {
        // Images are already uploaded to S3 (staged bucket) by the backend
        // Map them to our format - these URLs are already S3 URLs
        const candidates = response.images.map((img, index) => ({
          id: img.id || `extracted-${Date.now()}-${index}`,
          url: img.url, // This is already an S3 URL from backend
          title: img.title || `Extracted Image ${index + 1}`,
          source: 'extracted',
          selected: true // Auto-select extracted images (top 3)
        }));
        
        setExtractedImages(candidates);
        // Auto-confirm extracted images
        setConfirmedImages(candidates);
        showToast(`Successfully extracted ${candidates.length} image(s) from SerpAPI. They will be saved when you create the product.`, 'success');
      } else {
        showToast(response.message || 'No images found for this product', 'warning');
      }
    } catch (error) {
      console.error('Image extraction failed:', error);
      showToast('Image extraction failed: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setImageLoading(false);
    }
  };

  const handleUploadCustomImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    if (files.length > 3) {
      showToast('Maximum 3 images can be uploaded at once. Please select fewer images.', 'warning');
      return;
    }
    
    const selectedExtractedCount = extractedImages.filter(img => img.selected).length;
    const selectedCustomCount = customImages.filter(img => img.selected).length;
    const totalSelected = selectedExtractedCount + selectedCustomCount;
    
    const remainingSlots = 3 - totalSelected;
    if (remainingSlots <= 0) {
      showToast('Maximum 3 images allowed total. Please deselect some images first.', 'warning');
      return;
    }
    
    const filesToAdd = files.slice(0, remainingSlots);
    
    setUploadLoading(true);
    const newCustomImages = [];
    
    try {
      for (const file of filesToAdd) {
        newCustomImages.push({
          id: `custom-${Date.now()}-${Math.random()}`,
          file: file,
          url: null,
          title: file.name,
          source: 'custom',
          selected: true,
          isPending: true
        });
      }
      
      setCustomImages(prev => [...prev, ...newCustomImages]);
      
      if (files.length > remainingSlots) {
        showToast(`${remainingSlots} image(s) added (${files.length - remainingSlots} skipped - max 3 total).`, 'info');
      } else {
        showToast(`${newCustomImages.length} image(s) added.`, 'success');
      }
    } catch (error) {
      console.error('Image selection failed:', error);
      showToast('Failed to select images: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleToggleExtractedImage = (imageId) => {
    const selectedExtractedCount = extractedImages.filter(img => img.selected).length;
    const selectedCustomCount = customImages.filter(img => img.selected).length;
    const totalSelected = selectedExtractedCount + selectedCustomCount;
    const image = extractedImages.find(img => img.id === imageId);
    
    if (!image.selected && totalSelected >= 3) {
      showToast('Maximum 3 images allowed', 'warning');
      return;
    }
    
    setExtractedImages(prev => 
      prev.map(img => 
        img.id === imageId ? { ...img, selected: !img.selected } : img
      )
    );
  };

  const handleToggleCustomImage = (imageId) => {
    const selectedExtractedCount = extractedImages.filter(img => img.selected).length;
    const selectedCustomCount = customImages.filter(img => img.selected).length;
    const totalSelected = selectedExtractedCount + selectedCustomCount;
    const image = customImages.find(img => img.id === imageId);
    
    if (!image.selected && totalSelected >= 3) {
      showToast('Maximum 3 images allowed', 'warning');
      return;
    }
    
    setCustomImages(prev => 
      prev.map(img => 
        img.id === imageId ? { ...img, selected: !img.selected } : img
      )
    );
  };

  const handleRemoveExtractedImage = (imageId) => {
    setExtractedImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleRemoveCustomImage = (imageId) => {
    setCustomImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleConfirmImages = async () => {
    const selectedExtracted = extractedImages.filter(img => img.selected);
    const selectedCustom = customImages.filter(img => img.selected);
    
    if (selectedExtracted.length === 0 && selectedCustom.length === 0) {
      showToast('No images selected', 'warning');
      return;
    }
    
    const allSelectedImages = [...selectedExtracted, ...selectedCustom];
    setConfirmedImages(allSelectedImages);
    
    showToast(`Confirmed ${allSelectedImages.length} image(s) for product creation. They will be uploaded when you create the product.`, 'success');
  };

  // AI inference function
  const handleRunAI = async () => {
    if (!formData.description.trim()) {
      showToast('Please enter product description first', 'warning');
      return;
    }

    setAiLoading(true);
    try {
      const selectedManufacturer = manufacturers.find(mfr => mfr.id === Number(formData.mfr));
      const selectedCategory = categories.find(cat => cat.id === Number(formData.category));
      
      const brand = selectedManufacturer ? selectedManufacturer.name : formData.mfr.trim();
      const category = selectedCategory ? selectedCategory.name : '';
      
      const productDetails = {
        brand: brand,
        description: formData.description.trim(),
        sku: formData.part?.trim() || '',
        category: category
      };
      
      const response = await runAIInferenceByProductDetails(productDetails);
      
      if (response.success && response.data) {
        setAiInferenceData(response.data);
        
        // Always update fields with AI results (override existing values)
        const updates = {};
        if (response.data.weight !== undefined && response.data.weight !== null) {
          updates.weight = String(response.data.weight);
        }
        if (response.data.dimensions !== undefined && response.data.dimensions !== null) {
          updates.dimensions = String(response.data.dimensions);
        }
        if (response.data.ai_low !== undefined && response.data.ai_low !== null) {
          updates.ai_low = String(response.data.ai_low);
        }
        if (response.data.ai_high !== undefined && response.data.ai_high !== null) {
          updates.ai_high = String(response.data.ai_high);
        }
        
        setFormData(prev => ({ ...prev, ...updates }));
        
        showToast('AI inference completed successfully! Fields have been auto-populated.', 'success');
      } else {
        showToast(response.message || 'AI inference failed', 'error');
      }
    } catch (error) {
      console.error('AI inference failed:', error);
      showToast('AI inference failed: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    // Validate required fields for concatenate code
    if (!isFormValid) {
      const problems = [];
      if (!formData.vendor?.trim()) problems.push("Vendor");
      if (!formData.mfr?.trim()) problems.push("MFR (Brand)");
      if (!formData.part?.trim()) problems.push("Part # (SKU)");
      if (!formData.condition?.trim()) problems.push("Condition");
      
      const msg = `Required fields missing: ${problems.join(", ")}`;
      setError(msg);
      showToast(msg, "error");
      return;
    }

    // Additional validation
    if (!formData.description.trim()) {
      const msg = "Description is required";
      setError(msg);
      showToast(msg, "error");
      return;
    }

    // Map form → API payload
    const selectedCategory = categories.find(cat => cat.id === Number(formData.category));
    const selectedManufacturer = manufacturers.find(mfr => mfr.id === Number(formData.mfr));
    
    const payload = {
      brand: selectedManufacturer ? selectedManufacturer.name : formData.mfr.trim(),
      category: selectedCategory ? selectedCategory.name : '',
      description: formData.description.trim(),
      sku: formData.part?.trim() || null,
      condition: formData.condition,
      stock_quantity: toInt(formData.qty, 0),
      location: formData.location?.trim() || null,
      category_id: formData.category ? Number(formData.category) : null,
      manufacturer_id: formData.mfr ? Number(formData.mfr) : null,
      is_available: true,
      image_urls: [], // Will be set after product creation
      vendor: formData.vendor?.trim() || null,
      cost_price: toFloat(formData.cost_price),
      retail_price: toFloat(formData.retail_price),
      weight: toFloat(formData.weight),
      dimensions: formData.dimensions?.trim() || null,
      ai_low: toFloat(formData.ai_low),
      ai_high: toFloat(formData.ai_high),
      concatenate_code: formData.concatenate_code?.trim() || null,
      specifications: formData.specifications?.trim() || null,
      sub_part_number: formData.subpn?.trim() || null,
      sub_2_part_number: formData.sub2pn?.trim() || null,
    };

    setSaving(true);
    try {
      let saved;
      try {
        saved = await createProduct(payload);
      } catch (apiError) {
        // Re-throw API errors to be handled by outer catch
        throw apiError;
      }
      
      // Verify product was actually created
      if (!saved || !saved.id) {
        throw new Error("Product creation failed: No product ID returned from server");
      }
      
      // Upload and save images to S3 after product creation
      if (confirmedImages.length > 0 && saved.id) {
        try {
          const allImageUrls = [];
          
          // 1. Upload custom images (file objects) to S3
          const customImagesToUpload = confirmedImages.filter(img => img.file && img.isPending);
          for (const img of customImagesToUpload) {
            const response = await uploadCustomImage(saved.id, img.file);
            if (response.image_url) {
              allImageUrls.push(response.image_url);
            }
          }
          
          // 2. For extracted images, they're already in staged S3 bucket
          // We need to move them to product-images bucket and get final URLs
          const extractedImagesToProcess = confirmedImages.filter(img => img.url && !img.file);
          
          if (extractedImagesToProcess.length > 0) {
            // Extract images are already uploaded to S3 by extractImagesByProductDetails endpoint
            // They're in staged bucket, but we need to ensure they're in product-images bucket
            // The backend selectRegularProductImages endpoint should handle this
            const extractedUrls = extractedImagesToProcess
              .map(img => img.url)
              .filter(url => url && typeof url === 'string' && url.trim() !== '');
            
            allImageUrls.push(...extractedUrls);
          }
          
          // Limit to 3 images total
          const finalImageUrls = allImageUrls.slice(0, 3);
          
          // Save all image URLs to product (this will move staged images to product-images bucket)
          if (finalImageUrls.length > 0) {
            await selectRegularProductImages(saved.id, finalImageUrls);
            showToast(`Product created successfully with ${finalImageUrls.length} image(s) saved to product-images S3 bucket`, "success");
          } else {
            showToast("Product created successfully", "success");
          }
        } catch (uploadError) {
          console.error('Failed to upload images after product creation:', uploadError);
          // Still show success for product creation, but warn about images
          showToast('Product created but images failed to upload to S3', 'warning');
        }
      } else {
        showToast("Product created successfully", "success");
      }

      // Close the dialog first
      if (onCancel) {
        onCancel();
      }
      
      // Call onCreated callback if provided (this will refresh the list)
      if (onCreated) {
        onCreated(saved);
      }
      
      // Refresh the product management page after successful creation
      // Use setTimeout to ensure toast is shown and dialog closes before refresh
      setTimeout(() => {
        // Force page refresh to reload product management page
        window.location.href = '/product-management';
      }, 300);
    } catch (e) {
      // Don't close window on error
      let msg = "Product creation failed";
      
      console.error('Product creation error:', e);
      
      // Handle different error types
      // apiRequest throws errors with error.data or error.text
      if (e?.data?.detail) {
        const detail = e.data.detail;
        if (detail.includes("concatenate_code") && (detail.includes("already exists") || detail.includes("duplicate key"))) {
          msg = `Product with this concatenate_code already exists. Please change Vendor, MFR (Brand), Part # (SKU), or Condition to generate a unique concatenate_code.`;
        } else {
          msg = detail;
        }
      } else if (e?.text) {
        // Try to parse error text as JSON
        try {
          const errorData = JSON.parse(e.text);
          if (errorData?.detail) {
            const detail = errorData.detail;
            if (detail.includes("concatenate_code") && (detail.includes("already exists") || detail.includes("duplicate key"))) {
              msg = `Product with this concatenate_code already exists. Please change Vendor, MFR (Brand), Part # (SKU), or Condition to generate a unique concatenate_code.`;
            } else {
              msg = detail;
            }
          } else {
            msg = e.text;
          }
        } catch (parseError) {
          msg = e.text || e.message || "Product creation failed";
        }
      } else if (e?.message) {
        msg = e.message;
      } else if (typeof e === 'string') {
        msg = e;
      }
      
      setError(msg);
      showToast(msg, "error");
      setSaving(false); // Re-enable button on error
    }
  };

  // Render field helper
  const renderField = (field) => {
    const value = formData[field.key] ?? '';
    
    if (field.type === 'select-manufacturer') {
      return (
        <Grid 
          item 
          xs={field.xs} 
          key={field.key}
          sx={{
            width: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            flexBasis: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            maxWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            minWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)'
          }}
        >
          <TextField
            select
            label={field.label}
            name={field.key}
            value={value}
            onChange={handleChange}
            fullWidth
            size="small"
            required={field.required}
            disabled={loadingMfrs}
            sx={{ 
              width: '100% !important',
              minWidth: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                width: '100% !important'
              },
              '& .MuiInputBase-root': {
                width: '100% !important'
              }
            }}
          >
            <MenuItem value="">(none)</MenuItem>
            {manufacturers.map((mfr) => (
              <MenuItem key={mfr.id} value={String(mfr.id)}>
                {mfr.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      );
    }

    if (field.type === 'select-category') {
      return (
        <Grid 
          item 
          xs={field.xs} 
          key={field.key}
          sx={{
            width: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            flexBasis: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            maxWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            minWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)'
          }}
        >
          <TextField
            select
            label={field.label}
            name={field.key}
            value={value}
            onChange={handleChange}
            fullWidth
            size="small"
            disabled={loadingCats}
            sx={{ 
              width: '100% !important',
              minWidth: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                width: '100% !important'
              },
              '& .MuiInputBase-root': {
                width: '100% !important'
              }
            }}
          >
            <MenuItem value="">(none)</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={String(cat.id)}>
                {cat.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      );
    }

    if (field.type === 'select-condition') {
      return (
        <Grid 
          item 
          xs={field.xs} 
          key={field.key}
          sx={{
            width: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            flexBasis: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            maxWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            minWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)'
          }}
        >
          <TextField
            select
            label={field.label}
            name={field.key}
            value={value}
            onChange={handleChange}
            fullWidth
            size="small"
            required={field.required}
            sx={{ 
              width: '100% !important',
              minWidth: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                width: '100% !important'
              },
              '& .MuiInputBase-root': {
                width: '100% !important'
              }
            }}
          >
            <MenuItem value="">(none)</MenuItem>
            {CONDITION_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      );
    }

    if (field.type === 'select-vendor') {
      return (
        <Grid 
          item 
          xs={field.xs} 
          key={field.key}
          sx={{
            width: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            flexBasis: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            maxWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            minWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)'
          }}
        >
          <TextField
            select
            label={field.label}
            name={field.key}
            value={value}
            onChange={handleChange}
            fullWidth
            size="small"
            required={field.required}
            disabled={loadingVendors}
            sx={{ 
              width: '100% !important',
              minWidth: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                width: '100% !important'
              },
              '& .MuiInputBase-root': {
                width: '100% !important'
              }
            }}
          >
            <MenuItem value="">(none)</MenuItem>
            {vendors.map((vendor) => (
              <MenuItem key={vendor} value={vendor}>
                {vendor}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      );
    }

    if (field.type === 'textarea') {
      return (
        <Grid 
          item 
          xs={field.xs} 
          key={field.key}
          sx={{
            width: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            flexBasis: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            maxWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            minWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)'
          }}
        >
          <TextField
            label={field.label}
            name={field.key}
            value={value}
            onChange={handleChange}
            fullWidth
            multiline
            rows={field.key === 'concatenate_code' ? 2 : 4}
            size="small"
            InputProps={{
              readOnly: field.readOnly && generateConcatenateCode ? true : false,
            }}
            sx={{ 
              width: '100% !important',
              minWidth: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                width: '100% !important'
              },
              '& .MuiInputBase-root': {
                width: '100% !important'
              }
            }}
          />
        </Grid>
      );
    }

    // Regular text/number field
    return (
      <Grid 
        item 
        xs={field.xs} 
        key={field.key}
        sx={{
          width: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
          flexBasis: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
          maxWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
          minWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)'
        }}
      >
        <TextField
          label={field.label}
          name={field.key}
          value={value}
          onChange={handleChange}
          fullWidth
          size="small"
          type={field.type === 'number-int' ? 'number' : field.type === 'number-float' ? 'number' : 'text'}
          inputProps={field.type?.startsWith('number') ? { 
            min: 0,
            step: field.type === 'number-float' ? 0.01 : 1
          } : {}}
          sx={{ 
            width: '100% !important',
            minWidth: '100%',
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#fff',
              width: '100% !important'
            },
            '& .MuiInputBase-root': {
              width: '100% !important'
            }
          }}
        />
      </Grid>
    );
  };

  // Field rows matching Edit Product layout
  const fieldRows = useMemo(() => [
    // Row 1: Description (full width at top)
    [
      { label: 'Description', key: 'description', xs: 12, type: 'textarea' },
    ],
    // Row 2: MFR (Brand) | Category
    [
      { label: 'MFR (Brand)', key: 'mfr', xs: 6, type: 'select-manufacturer', required: true },
      { label: 'Category', key: 'category', xs: 6, type: 'select-category' },
    ],
    // Row 3: Condition | Part # (SKU)
    [
      { label: 'Condition', key: 'condition', xs: 6, type: 'select-condition', required: true },
      { label: 'Part # (SKU)', key: 'part', xs: 6, required: true },
    ],
    // Row 4: Vendor | Location
    [
      { label: 'Vendor', key: 'vendor', xs: 6, type: 'select-vendor', required: true },
      { label: 'Location', key: 'location', xs: 6 },
    ],
    // Row 5: Weight | Dimensions
    [
      { label: 'Weight', key: 'weight', xs: 6, type: 'number-float' },
      { label: 'Dimensions', key: 'dimensions', xs: 6 },
    ],
    // Row 6: Qty (stock_quantity) | (empty placeholder)
    [
      { label: 'Qty (stock_quantity)', key: 'qty', xs: 6, type: 'number-int' },
    ],
    // Row 7: Cost Price | Retail/Current Price
    [
      { label: 'Cost Price', key: 'cost_price', xs: 6, type: 'number-float' },
      { label: 'Retail/Current Price', key: 'retail_price', xs: 6, type: 'number-float' },
    ],
    // Row 8: AI Low : $ | AI High : $
    [
      { label: 'AI Low : $', key: 'ai_low', xs: 6, type: 'number-float' },
      { label: 'AI High : $', key: 'ai_high', xs: 6, type: 'number-float' },
    ],
    // Row 9: Sub P/N | Sub 2 P/N
    [
      { label: 'Sub P/N', key: 'subpn', xs: 6 },
      { label: 'Sub 2 P/N', key: 'sub2pn', xs: 6 },
    ],
    // Row 10: Concatenate Code (full width, read-only when auto-generated)
    [
      { label: 'Concatenate Code', key: 'concatenate_code', xs: 12, type: 'textarea', readOnly: true },
    ],
    // Row 11: Specifications (full width)
    [
      { label: 'Specifications (Long Description)', key: 'specifications', xs: 12, type: 'textarea' },
    ],
  ], []);

  return (
    <FormContainer>
      <Box sx={{ mb: 2 }}>
        <HeaderTitle variant="h6">Create a New Product</HeaderTitle>
      </Box>

      {error ? (
        <div style={{ color: "red", marginBottom: 12, fontSize: 13 }}>{error}</div>
      ) : null}

      <Grid container spacing={3} sx={{ width: '100%' }}>
        {/* Render all field rows in strict 2-column layout */}
        {fieldRows.map((row, rowIndex) => (
          <Grid 
            container 
            spacing={3} 
            key={`row-${rowIndex}`} 
            sx={{ 
              width: '100%',
              mb: rowIndex < fieldRows.length - 1 ? 2 : 0,
              boxSizing: 'border-box',
              '& > .MuiGrid-item': {
                boxSizing: 'border-box',
                '&[data-xs="6"]': {
                  flexBasis: 'calc(50% - 12px) !important',
                  maxWidth: 'calc(50% - 12px) !important',
                  width: 'calc(50% - 12px) !important',
                  minWidth: 'calc(50% - 12px) !important'
                },
                '&[data-xs="12"]': {
                  flexBasis: '100% !important',
                  maxWidth: '100% !important',
                  width: '100% !important'
                }
              }
            }}
          >
            {row.map(renderField)}
          </Grid>
        ))}

        {/* Product Images Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            mt: 4, 
            mb: 3, 
            p: 3, 
            backgroundColor: '#f8f9fa', 
            borderRadius: 3,
            border: '1px solid #e9ecef'
          }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#495057' }}>
              Product Images & AI Enhancement
            </Typography>
          
            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={handleRunAI}
                disabled={aiLoading || !formData.description.trim()}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: 140
                }}
              >
                {aiLoading ? 'Running AI...' : 'Run Akini AI'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleExtractImages}
                disabled={imageLoading || !formData.description.trim()}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: 140
                }}
              >
                {imageLoading ? 'Extracting...' : 'Extract Images'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleUploadCustomImage}
                disabled={uploadLoading}
                startIcon={<CloudUploadIcon />}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: 160
                }}
              >
                {uploadLoading ? 'Uploading...' : 'Upload Custom Image'}
              </Button>
              
              {(extractedImages.length > 0 || customImages.length > 0) && (
                <Button
                  variant="contained"
                  onClick={handleConfirmImages}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    minWidth: 140
                  }}
                >
                  Confirm Images
                </Button>
              )}
            </Box>

            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              multiple
              style={{ display: 'none' }}
            />

            {/* Image Display */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              {/* Extracted Images */}
              {extractedImages.map((image) => (
                <Card 
                  key={image.id} 
                  sx={{ 
                    width: 160, 
                    height: 160, 
                    border: image.selected ? '3px solid #4caf50' : '3px solid #1976d2',
                    cursor: 'pointer',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => handleToggleExtractedImage(image.id)}
                >
                  <CardMedia
                    component="img"
                    height="160"
                    image={image.url}
                    alt={image.title}
                    sx={{ objectFit: 'cover' }}
                  />
                  
                  <div 
                    style={{
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      backgroundColor: '#f44336',
                      color: 'white',
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveExtractedImage(image.id);
                    }}
                  >
                    ×
                  </div>
                  
                  <div style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: image.selected ? '#4caf50' : '#1976d2',
                    color: 'white',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 'bold'
                  }}>
                    {image.selected ? '✓' : '○'}
                  </div>
                </Card>
              ))}

              {/* Custom Images */}
              {customImages.map((image) => {
                const imageUrl = image.url || (image.file ? URL.createObjectURL(image.file) : null);
                
                if (!imageUrl) return null;
                
                return (
                <Card 
                  key={image.id} 
                  sx={{ 
                    width: 160, 
                    height: 160, 
                    border: image.selected ? '3px solid #4caf50' : '3px solid #1976d2',
                    cursor: 'pointer',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => handleToggleCustomImage(image.id)}
                >
                  <CardMedia
                    component="img"
                    height="160"
                    image={imageUrl}
                    alt={image.title}
                    sx={{ objectFit: 'cover' }}
                  />
                  
                  <div 
                    style={{
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      backgroundColor: '#f44336',
                      color: 'white',
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCustomImage(image.id);
                    }}
                  >
                    ×
                  </div>
                  
                  <div style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: image.selected ? '#4caf50' : '#1976d2',
                    color: 'white',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 'bold'
                  }}>
                    {image.selected ? '✓' : '○'}
                  </div>
                </Card>
              );
              })}
            </Box>

            {/* AI Inference Success Summary */}
            {aiInferenceData && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 2, border: '1px solid #2196f3' }}>
                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                  ✓ AI inference completed! Product details have been auto-populated.
                </Typography>
              </Box>
            )}

            {/* Confirmed Images Summary */}
            {confirmedImages.length > 0 && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8f5e8', borderRadius: 2 }}>
                <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                  ✓ {confirmedImages.length} image(s) confirmed for product creation
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
          <ActionButton
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            disabled={saving || !isFormValid}
            sx={{
              opacity: (saving || !isFormValid) ? 0.6 : 1,
              cursor: (saving || !isFormValid) ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? "Creating…" : "Create"}
          </ActionButton>
        </Grid>
      </Grid>
    </FormContainer>
  );
}
